import { DateTime } from 'luxon';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CELL_HEIGHT, COLOR_ITEM, COUNT_HOURS, FORMAT_DATE, PARTS_IN_HOUR } from '../const';
import { getStartOfWeek, getTimeFromNumberRow } from '../functions';
import useCommitTask from '../hooks/useCommitTask';
import useEffectBuffer from '../hooks/useEffectBuffer';
import useScreenWidth from '../hooks/useScreenWidth';
import useShiftKey from '../hooks/useShiftKey';
import useTasks from '../hooks/useTasks';
import { ComponentsDispatch, RootState } from '../store';
import { setterCalendar } from '../store/slices/calendarSlice';
import { DayWeekType, ItemTaskType, TaskPositionType, TaskType } from '../types';
import CalendarTask from './calendarTask';

interface ICalendarTaskList {
    typeDayWeek: DayWeekType;
}

const CalendarTaskList: React.FC<ICalendarTaskList> = ({ typeDayWeek }) => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const listTaskForDayWeek = useTasks(typeDayWeek);
    const currentDate = useSelector<RootState, string>((state) => state.calendar.currentDate);
    const isDragging = useSelector<RootState, boolean>((state) => state.calendar.isDragging);
    const listTask = useSelector<RootState, TaskType[]>((state) => state.calendar.listTask);
    const positioningTasks = useSelector<RootState, TaskPositionType[]>((state) => state.calendar.positioningTasks);
    const isShiftPressed = useShiftKey();
    const { addTaskToDataBase } = useCommitTask(typeDayWeek);

    const draggableContainerRef = useRef<HTMLDivElement>(null);
    const [cellWidth, setCellWidth] = useState<number>(0);

    const screenWidth = useScreenWidth();

    useEffect(() => {
        dispatch(setterCalendar({ listTask: listTaskForDayWeek }));
    }, [listTaskForDayWeek]);

    useEffectBuffer(
        () => {
            setCellWidth(
                draggableContainerRef.current
                    ? typeDayWeek === 'day'
                        ? draggableContainerRef.current.clientWidth
                        : draggableContainerRef.current.clientWidth / 7
                    : 0,
            );
        },
        100,
        [screenWidth],
    );

    useEffect(() => {
        const groupTask: {
            [key: string]: {
                minRow: number;
                maxRow: number;
                tasks: TaskType[];
            }[];
        } = {};

        const orderTasks = [...listTask].sort((taskFirst, taskSecond) => (taskFirst.row >= taskSecond.row ? 1 : -1));

        orderTasks.forEach((task) => {
            let isChanged = false;

            if (groupTask[task.datetime]) {
                for (let i = 0; i < groupTask[task.datetime].length; i++) {
                    if (
                        task.row >= groupTask[task.datetime][i].minRow &&
                        task.row < groupTask[task.datetime][i].maxRow
                    ) {
                        groupTask[task.datetime][i].maxRow = Math.max(
                            task.row + task.height,
                            groupTask[task.datetime][i].maxRow,
                        );
                        groupTask[task.datetime][i].tasks.push(task);
                        isChanged = true;
                        break;
                    }
                }
            }

            if (!isChanged) {
                if (!groupTask[task.datetime]) {
                    groupTask[task.datetime] = [];
                }

                groupTask[task.datetime].push({
                    maxRow: task.row + task.height,
                    minRow: task.row,
                    tasks: [task],
                });
            }
        });

        let newTasks: TaskPositionType[] = [];

        for (const key in groupTask) {
            for (const group of groupTask[key]) {
                const countColumnsStore: { [key: string]: number } = {};

                let maxCountColumn = 1;

                group.tasks.forEach((task) => {
                    const keyTime = task.start_datetime;

                    if (!countColumnsStore[keyTime]) {
                        countColumnsStore[keyTime] = 1;
                    } else {
                        countColumnsStore[keyTime]++;
                        maxCountColumn = Math.max(maxCountColumn, countColumnsStore[keyTime]);
                    }
                });

                const getCountParent = (task: TaskPositionType, colShow: number): number => {
                    const parentTask = newTasks
                        .filter(
                            (subTask) =>
                                task.datetime === subTask.datetime &&
                                task.row > subTask.row &&
                                task.row < subTask.row + subTask.height &&
                                subTask.colShow === colShow,
                        )
                        .pop();

                    if (parentTask) {
                        return getCountParent(parentTask, colShow) + 1;
                    }
                    return 0;
                };

                let colShow = 0;
                group.tasks.forEach((task, index) => {
                    if (colShow === maxCountColumn) {
                        colShow = 1;
                    } else {
                        colShow++;
                    }

                    const newTask: TaskPositionType = {
                        ...task,
                        colShow,
                        colCount: maxCountColumn,
                        zIndex: index * 10,
                        marginLeft: 0,
                    };

                    const levelMargin = getCountParent(newTask, colShow);

                    newTask.marginLeft = levelMargin * 10;
                    newTasks.push(newTask);
                });
            }
        }

        newTasks = listTask.map(
            (task) => newTasks.filter((subTask) => subTask.id === task.id).map((subTask) => ({ ...subTask }))[0],
        );

        dispatch(setterCalendar({ positioningTasks: newTasks }));
    }, [listTask]);

    const addNewTask = (e: MouseEvent) => {
        if (!draggableContainerRef.current || isDragging || !isShiftPressed) {
            return;
        }

        const rect = draggableContainerRef.current?.getBoundingClientRect();

        const numberCol = Math.ceil((e.clientX - rect.left) / cellWidth) - 1;
        const numberRow = Math.ceil((e.clientY - rect.top) / CELL_HEIGHT) - 1;

        const startWeekDate =
            typeDayWeek === 'week' ? getStartOfWeek(DateTime.fromISO(currentDate)) : DateTime.fromISO(currentDate);
        const newDateTask = startWeekDate.plus({ day: numberCol });

        const newTask = {
            color: COLOR_ITEM,
            start_datetime: getTimeFromNumberRow(numberRow),
            end_datetime: getTimeFromNumberRow(numberRow + 2),
            datetime: newDateTask.toFormat(FORMAT_DATE),
            name: 'new task',
            assignee_for_task: [] as string[],
        } as ItemTaskType;

        addTaskToDataBase(newTask);
    };

    return (
        <>
            <div
                ref={draggableContainerRef}
                style={{
                    position: 'absolute',
                    left: 60,
                    cursor: isShiftPressed ? 'crosshair' : 'auto',
                    height: CELL_HEIGHT * COUNT_HOURS * PARTS_IN_HOUR,
                    top: 0,
                    right: 0,
                    display: 'flex',
                    flexDirection: 'column',
                }}
                onClick={addNewTask as any}
            >
                {positioningTasks.map((task) => {
                    return <CalendarTask key={task.id} typeDayWeek={typeDayWeek} task={task} cellWidth={cellWidth} />;
                })}
            </div>
        </>
    );
};

export default memo(CalendarTaskList);
